import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { useLoginMutation } from '@store/api/authApi';
import { loginStart, loginSuccess, loginFailure } from '@store/slices/authSlice';
import { AUTH_STORAGE_KEYS } from '../../config/auth';

interface LoginFormData {
  username: string;
  password: string;
  rememberMe: boolean;
}

const loginSchema = yup.object().shape({
  username: yup.string().required('Username is required'),
  password: yup.string().required('Password is required').min(6, 'Password must be at least 6 characters'),
  rememberMe: yup.boolean().default(false),
});

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isAuthenticated, isLoading, error } = useAppSelector((state) => state.auth);
  const [showPassword, setShowPassword] = useState(false);
  const [login] = useLoginMutation();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
      rememberMe: false,
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    dispatch(loginStart());

    try {
      const response = await login(data).unwrap();

      // Store auth data in localStorage (will be moved to httpOnly cookies later)
      localStorage.setItem(AUTH_STORAGE_KEYS.token, response.token);
      localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, response.refreshToken);
      localStorage.setItem(AUTH_STORAGE_KEYS.tokenExpiresAt, response.expiresAt);
      // Store user data for restoration
      localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(response.user));

      dispatch(loginSuccess({
        user: response.user,
        token: response.token,
        refreshToken: response.refreshToken,
        expiresAt: response.expiresAt,
      }));

      navigate('/');
    } catch (err: any) {
      // RTK Query unwrap() throws the response body directly
      const errorMessage = err?.detail || err?.data?.detail || err?.message || err?.data?.message || 'Login failed. Please try again.';
      dispatch(loginFailure(errorMessage));
    }
  };

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        padding: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          padding: { xs: 3, sm: 4 },
          width: '100%',
          maxWidth: 440,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            component="img"
            src="/assets/comnovo-logo.svg"
            alt="Comnovo"
            sx={{ height: 48, width: 'auto', mb: 2 }}
          />
          <Typography variant="h4" component="h1" gutterBottom>
            IoT Device Management
          </Typography>
          <Typography variant="body1" sx={{
            color: 'text.secondary'
          }}>
            Sign in to your account
          </Typography>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Controller
            name="username"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Username"
                fullWidth
                margin="normal"
                error={!!errors.username}
                helperText={errors.username?.message}
                autoComplete="username"
                autoFocus
              />
            )}
          />

          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Password"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                margin="normal"
                error={!!errors.password}
                helperText={errors.password?.message}
                autoComplete="current-password"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }
                }}
              />
            )}
          />

          <Controller
            name="rememberMe"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label="Remember me"
                sx={{ mt: 1, mb: 2 }}
              />
            )}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading}
            sx={{ mt: 2, mb: 2 }}
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </Button>

          <Box sx={{ textAlign: 'center' }}>
            <Link
              href="/forgot-password"
              underline="hover"
              sx={{ fontSize: '0.875rem' }}
            >
              Forgot your password?
            </Link>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default LoginPage;
